import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Form, Formik } from "formik";
import { ChangeEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Block, BlockPOST } from "pwapi";

import {
  createBlock,
  updateBlock,
  deleteBlock,
} from "shared/actions/admin/adminBlockActions";
import SegmentedControl, {
  onChangeForPieceCount,
  optionsForPieceCount,
} from "shared/components/SegmentedControl";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { fullNameForBlock } from "shared/utils/blockUtils";

import AdminPanel from "components/admin/adminPanel/AdminPanel";
import HalfScreenPanel from "components/admin/editPanels/halfScreenEditPanel/HalfScreenPanel";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import TextInput from "components/customInputs/TextInput";
import DjangoLink from "components/DjangoLink";
import ApplicationState from "store/ApplicationState";

import { shallowBlockComparison } from "./blockFormUtils";
import PopUpDialog from "../../PopUpDialog";

type BlockPanelProps = {
  returnUrl: string;
  adminCase: CaseAdminDetail;
  specimenId: number;
  resetErrorMessage: Function;
  resetDeletionStatus: Function;
  canUpdateSpecimen?: boolean;
  blockId?: number;
  isNew?: boolean;
};

const selectBlockById = (state: ApplicationState, id?: number) => {
  return state.admin.case.blocks.find((block: any) => block.id === id);
};

const BlockPanel = ({
  returnUrl,
  adminCase,
  specimenId,
  blockId,
  isNew,
}: BlockPanelProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const block = useSelector((state: ApplicationState) =>
    selectBlockById(state, blockId)
  );
  const specimen = adminCase.specimens?.find((spec) => spec.id === specimenId);
  const [isOpenWarningDialog, setIsOpenWarningDialog] =
    useState<boolean>(false);
  const [hasDeleteError, setHasDeleteError] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const onClose = () => {
    navigate(returnUrl, { replace: true });
  };

  const onDelete = () => {
    setIsOpenWarningDialog(true);
  };

  const onDeleteConfirm = () => {
    dispatch(
      deleteBlock({
        id: block.id,
      })
    )
      .then(() => {
        setIsOpenWarningDialog(false);
        onClose();
      })
      .catch(() => {
        setHasDeleteError(true);
      });
  };

  const handleOnSubmit = (values: Block) => {
    dispatch(
      isNew
        ? createBlock({
            requestBody: {
              name: values.name || null,
              piece_count: values.piece_count || null,
              lab_station_id: specimen?.lab_station
                ? specimen.lab_station.id
                  ? specimen.lab_station.id
                  : null
                : null,
              specimen_id: specimenId,
            } as BlockPOST,
          })
        : updateBlock({
            id: block.id,
            //@ts-ignore Block requires a name, but not for the patch
            requestBody: shallowBlockComparison(block, values),
          })
    )
      .then(() => {
        onClose();
      })
      .catch((e) => {
        if (e.body && typeof e.body === "object") {
          // TODO: Unit tests, and make this a utility function
          const errorKeys = Object.keys(e.body);
          setNameError(e.body[errorKeys[0]]);
        } else {
          setNameError(
            "An error occurred while saving this block. A possible cause could be a duplicate block name for the same specimen."
          );
        }
      });
  };

  const slidesInBlock = adminCase.slides.filter(
    (slide) => slide.block_obj_id === block?.id
  );

  // if editing a block, but the block is not found, return null.
  // This can happen if the block is deleted while the user is editing it.
  if (!isNew && !block) return null;

  return (
    <>
      {isOpenWarningDialog && (
        <PopUpDialog
          title={`Delete Block ${fullNameForBlock(block, specimen)}`}
          onClose={() => {
            setIsOpenWarningDialog(false);
          }}
          type="warning"
        >
          <div className="CaseWarningDialog">
            {!!slidesInBlock.length ? (
              <p>
                Block {fullNameForBlock(block, specimen)} cannot be deleted
                until all slides are removed from the block.
                <br />
                <br />
                There{" "}
                {slidesInBlock.length === 1
                  ? "is 1 slide"
                  : `are ${slidesInBlock.length} slides`}{" "}
                still associated with this block.
              </p>
            ) : (
              <p>
                Are you sure you want to delete block{" "}
                {fullNameForBlock(block, specimen)} on Specimen{" "}
                {specimen?.name || specimenId}?
                <br />
                <br />
                This action cannot be undone.
              </p>
            )}
            {hasDeleteError && (
              <p className="errorMessage">
                Unexpected error occurred while attempting to delete this block.{" "}
                Please contact support to identify the issue.
                <br />
                <a href="mailto@support@pathologywatch.com">
                  support@pathologywatch.com
                </a>
              </p>
            )}
            <div className="actions__container">
              <PathologyWatchButton
                onClick={() => setIsOpenWarningDialog(false)}
              >
                Cancel
              </PathologyWatchButton>
              <PathologyWatchButton
                type="submit"
                color="red"
                disabled={!!slidesInBlock.length}
                testId="block_confirm_delete"
                onClick={() => onDeleteConfirm()}
              >
                Delete
              </PathologyWatchButton>
            </div>
          </div>
        </PopUpDialog>
      )}
      <HalfScreenPanel onClose={onClose}>
        <div className="HalfScreen__container">
          <AdminPanel
            title={{
              prefix: isNew
                ? `Add Block to Specimen ${specimen?.name || specimenId}`
                : `Edit Block${
                    block.name ? ` ${block.name}` : ""
                  } on Specimen ${specimen?.name || specimenId}`,
              main: isNew
                ? "Create New Block"
                : `Edit Block ${fullNameForBlock(block, specimen)}`,
            }}
            djangoLink={
              <DjangoLink
                route={`histoslide/block${isNew ? "" : `/${block.id}/change/`}`}
                title="Django Block Access"
              />
            }
          >
            <Formik
              initialValues={{
                name: block?.name || "",
                piece_count: block?.piece_count,
              }}
              onSubmit={(values) => handleOnSubmit(values)}
            >
              <Form className="AdminPanelForm">
                <div className="panelContainer">
                  <fieldset className="panelContainer__section">
                    <Field name="name">
                      {({ field }: any) => (
                        <div>
                          <div className="panelContainer__inputGroup">
                            <CustomInput label="Specimen">
                              <strong>{specimen?.name || ""}</strong>
                            </CustomInput>
                            <TextInput
                              label="Block Number"
                              id="name"
                              maxLength={5}
                              {...field}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setNameError(
                                  specimen?.name &&
                                    e.target.value
                                      ?.toLowerCase()
                                      .startsWith(specimen.name.toLowerCase())
                                    ? "Block number should not begin with the specimen name"
                                    : null
                                );
                                field.onChange(e);
                              }}
                            />
                            <CustomInput label="Block Name Preview">
                              <strong data-testid="full_name_preview">
                                {specimen?.name || ""}
                                {field.value.toUpperCase()}
                              </strong>
                            </CustomInput>
                          </div>
                          {nameError && (
                            <span className="error Block__error">
                              <FontAwesomeIcon icon="exclamation-circle" />{" "}
                              {nameError}
                            </span>
                          )}
                        </div>
                      )}
                    </Field>
                    <Field name="piece_count">
                      {({ field, form }: any) => (
                        <>
                          <label>Gross Piece Count</label>
                          <SegmentedControl
                            id="piece_count"
                            options={optionsForPieceCount(
                              Object.values(Block.piece_count),
                              field.value
                            )}
                            name={`piece_count_${block?.id || "new"}`}
                            {...field}
                            onChange={onChangeForPieceCount(
                              field.value,
                              (pieceCount) =>
                                form.setFieldValue("piece_count", pieceCount)
                            )}
                          />
                        </>
                      )}
                    </Field>
                  </fieldset>
                </div>
                <footer>
                  <div className="panelContainer__inputGroup">
                    <PathologyWatchButton
                      onClick={() => {
                        onClose();
                      }}
                    >
                      Cancel
                    </PathologyWatchButton>
                    {!isNew && (
                      <PathologyWatchButton color="red" onClick={onDelete}>
                        Delete
                      </PathologyWatchButton>
                    )}
                  </div>
                  <PathologyWatchButton type="submit" color="green">
                    Save
                  </PathologyWatchButton>
                </footer>
              </Form>
            </Formik>
          </AdminPanel>
        </div>
      </HalfScreenPanel>
    </>
  );
};

export default BlockPanel;
